/**
 * Form primitives, and the one piece of shared error handling that matters.
 *
 * The API returns `{ error: { code, message, details, statute } }`. `FormErrors`
 * below renders the three cases that need different treatment:
 *
 *   VALIDATION  zod issues, with a `path` — shown against the field
 *   COMPLIANCE  a Michigan statutory rule, with a `statute` — shown prominently,
 *               because "deposit exceeds 1.5x rent" is a legal problem and not a
 *               typo, and staff need the citation to look it up
 *   everything   one message at the top
 *
 * Collapsing COMPLIANCE into ordinary validation is the mistake this component
 * exists to prevent: it is the difference between "fix the number" and "this lease
 * cannot legally be written this way".
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { ApiError } from '../lib/api.js';

interface FieldProps {
  label: string;
  name: string;
  children?: ReactNode;
  hint?: string;
  error?: string | undefined;
  required?: boolean;
}

export function Field({ label, name, children, hint, error, required }: FieldProps) {
  return (
    <label className="field" htmlFor={name}>
      <span className="field__label">
        {label}
        {required ? ' *' : ''}
      </span>
      {children}
      {hint ? <span className="muted">{hint}</span> : null}
      {error ? <span className="alert alert--error">{error}</span> : null}
    </label>
  );
}

interface TextInputProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'tel' | 'number' | 'date';
  placeholder?: string;
  required?: boolean;
  step?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
}

export function TextInput({ name, value, onChange, type = 'text', ...rest }: TextInputProps) {
  return (
    <input
      id={name}
      name={name}
      className="field__input"
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      {...rest}
    />
  );
}

interface PasswordInputProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  required?: boolean;
  disabled?: boolean;
}

export function PasswordInput({
  name,
  value,
  onChange,
  autoComplete,
  required,
  disabled,
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false);

  return (
    <span className="field__password">
      <input
        id={name}
        name={name}
        className="field__input field__input--password"
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        disabled={disabled}
      />
      <button
        type="button"
        className="field__password-toggle"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        title={visible ? 'Hide password' : 'Show password'}
      >
        {visible ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
      </button>
    </span>
  );
}

interface SelectProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly string[];
  /** Turns SINGLE_FAMILY into "Single family" for display. */
  humanize?: boolean;
}

export function Select({ name, value, onChange, options, humanize = true }: SelectProps) {
  return (
    <select
      id={name}
      name={name}
      className="field__input"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {humanize ? humanizeEnum(option) : option}
        </option>
      ))}
    </select>
  );
}

export function humanizeEnum(value: string): string {
  const lower = value.toLowerCase().replace(/_/g, ' ');
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

interface TextAreaProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
}

export function TextArea({ name, value, onChange, rows = 4 }: TextAreaProps) {
  return (
    <textarea
      id={name}
      name={name}
      className="field__input"
      rows={rows}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

/** zod issue shape, as the error handler serializes it. */
interface ValidationDetail {
  path: string;
  message: string;
}

/**
 * Pull per-field messages out of an ApiError so a form can show them inline.
 *
 * Returns a map keyed by the zod path. Nested paths arrive dotted
 * (`address.postalCode`), which is why forms key their fields the same way.
 */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError) || error.code !== 'VALIDATION') return {};
  if (!Array.isArray(error.details)) return {};

  const result: Record<string, string> = {};
  for (const detail of error.details as ValidationDetail[]) {
    if (typeof detail?.path === 'string' && typeof detail.message === 'string') {
      // First message per field. zod can emit several for one path and stacking them
      // in a form field is noise.
      result[detail.path] ??= detail.message;
    }
  }
  return result;
}

/** Top-of-form errors: everything that is not attributable to a single field. */
export function FormErrors({ error }: { error: unknown }) {
  if (!error) return null;

  if (error instanceof ApiError) {
    // A statutory refusal. Rendered with the citation because staff will be asked to
    // justify it, and "MCL 554.602" is what they need to hand to an attorney.
    if (error.code === 'COMPLIANCE') {
      return (
        <p className="alert alert--error">
          <strong>Michigan compliance</strong>
          {error.statute ? <> · {error.statute}</> : null}
          <br />
          {error.message}
        </p>
      );
    }

    // Field-level messages are rendered against their fields; this covers the
    // case where none of the paths matched a field the form actually renders,
    // which would otherwise fail silently.
    if (error.code === 'VALIDATION' && Object.keys(fieldErrors(error)).length > 0) {
      return null;
    }

    return <p className="alert alert--error">{error.message}</p>;
  }

  return (
    <p className="alert alert--error">
      {error instanceof Error ? error.message : 'Something went wrong.'}
    </p>
  );
}
